import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Send, Image, Smile, MoreVertical, Receipt, CreditCard, X, Upload, Search, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const EMOJI_LIST = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '💰', '🙏', '👏', '💯', '✨'];
const STICKERS = [
  { emoji: '🎊', label: 'Party' },
  { emoji: '🥳', label: 'Celebrate' },
  { emoji: '💸', label: 'Money' },
  { emoji: '🤑', label: 'Rich' },
  { emoji: '👏', label: 'Clap' },
  { emoji: '💪', label: 'Strong' },
  { emoji: '🙌', label: 'Hands' },
  { emoji: '✨', label: 'Sparkle' },
  { emoji: '🎯', label: 'Target' },
  { emoji: '🏆', label: 'Trophy' },
  { emoji: '💎', label: 'Diamond' },
  { emoji: '🚀', label: 'Rocket' }
];

// Popular GIFs for quick access (fallback when search is empty)
const TRENDING_GIFS = [
  'https://media.tenor.com/images/5d2d298a33e0e8c8b1a9c2e4d9f9c7f4/tenor.gif',
  'https://media.tenor.com/images/7d2e4f4c9c8f7e6d5c4b3a2f1e0d9c8b/tenor.gif',
];

const GroupChat = ({ user }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [group, setGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [showStickers, setShowStickers] = useState(false);
  const [showGifs, setShowGifs] = useState(false);
  const [gifSearch, setGifSearch] = useState('');
  const [gifs, setGifs] = useState([]);
  const [loadingGifs, setLoadingGifs] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imageCaption, setImageCaption] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pickerTab, setPickerTab] = useState('emoji'); // emoji, sticker, gif

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchMessages, 5000); // Poll for new messages
    return () => clearInterval(interval);
  }, [groupId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchData = async () => {
    try {
      const [groupRes, messagesRes] = await Promise.all([
        axios.get(`${API}/groups/${groupId}`),
        axios.get(`${API}/chat/${groupId}/messages?limit=100`)
      ]);
      setGroup(groupRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      toast.error('Failed to load chat');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/chat/${groupId}/messages?limit=100`);
      setMessages(res.data);
    } catch (error) {
      // Silent fail for polling
    }
  };

  const sendMessage = async (content, type = 'text', mediaUrl = null) => {
    if (!content.trim() && type === 'text') return;
    
    setSending(true);
    try {
      const params = new URLSearchParams({
        user_id: user.id,
        content: content,
        message_type: type
      });
      if (mediaUrl) params.append('media_url', mediaUrl);
      
      await axios.post(`${API}/chat/${groupId}/messages?${params.toString()}`);
      setMessageText('');
      setShowEmojis(false);
      setShowStickers(false);
      setShowGifs(false);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Fetch GIFs from Tenor API
  const searchGifs = async (query) => {
    if (!query.trim()) {
      setGifs([]);
      return;
    }
    
    setLoadingGifs(true);
    try {
      // Using Tenor's anonymous API
      const response = await axios.get(
        `https://tenor.googleapis.com/v2/search?q=${encodeURIComponent(query)}&key=AIzaSyAyimkuYQYF_FXVALexPuGQctUWRURdCYQ&limit=20&media_filter=gif`
      );
      const gifResults = response.data.results?.map(gif => ({
        id: gif.id,
        url: gif.media_formats?.gif?.url || gif.media_formats?.tinygif?.url,
        preview: gif.media_formats?.tinygif?.url || gif.media_formats?.gif?.url,
        title: gif.content_description
      })) || [];
      setGifs(gifResults);
    } catch (error) {
      console.error('GIF search error:', error);
      // Fallback to a curated list of popular GIFs
      setGifs([]);
    } finally {
      setLoadingGifs(false);
    }
  };

  // Debounced GIF search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showGifs && gifSearch) {
        searchGifs(gifSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [gifSearch, showGifs]);

  const sendGif = (gifUrl) => {
    sendMessage('', 'gif', gifUrl);
    setShowGifs(false);
    setGifSearch('');
    setGifs([]);
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      await axios.post(`${API}/chat/messages/${messageId}/react?user_id=${user.id}&emoji=${encodeURIComponent(emoji)}`);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to add reaction');
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image (JPG, PNG, GIF, WEBP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Max size is 5MB');
      return;
    }

    setSelectedImage(file);
    setImagePreviewUrl(URL.createObjectURL(file));
    setShowImagePreview(true);
  };

  const handleImageUpload = async () => {
    if (!selectedImage) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedImage);

      const response = await axios.post(
        `${API}/chat/${groupId}/upload-image?user_id=${user.id}${imageCaption ? `&caption=${encodeURIComponent(imageCaption)}` : ''}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      toast.success('Image sent!');
      setShowImagePreview(false);
      setSelectedImage(null);
      setImagePreviewUrl(null);
      setImageCaption('');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const cancelImageUpload = () => {
    setShowImagePreview(false);
    setSelectedImage(null);
    setImagePreviewUrl(null);
    setImageCaption('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(messageText);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950 flex flex-col">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-xl border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/group/${groupId}`)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-semibold">{group?.name}</h1>
              <p className="text-xs text-muted-foreground">{group?.member_ids?.length} members</p>
            </div>
          </div>
          <Button variant="ghost" size="icon">
            <MoreVertical className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 max-w-4xl mx-auto w-full">
        <div className="space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Send className="w-10 h-10 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            <AnimatePresence>
              {messages.map((message, index) => {
                const isOwn = message.user_id === user.id;
                const isSystemMessage = message.message_type === 'expense' || message.message_type === 'settlement';
                
                if (isSystemMessage) {
                  return (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex justify-center"
                    >
                      <div className="bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 px-4 py-2 rounded-full text-sm flex items-center gap-2">
                        {message.message_type === 'expense' ? <Receipt className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
                        {message.content}
                      </div>
                    </motion.div>
                  );
                }
                
                return (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] ${isOwn ? 'order-2' : 'order-1'}`}>
                      {!isOwn && (
                        <p className="text-xs text-muted-foreground mb-1 ml-3">{message.user_name}</p>
                      )}
                      <div
                        className={`chat-bubble ${isOwn ? 'chat-bubble-sent' : 'chat-bubble-received'}`}
                      >
                        {message.message_type === 'sticker' ? (
                          <span className="text-4xl">{message.content}</span>
                        ) : message.message_type === 'gif' ? (
                          <img 
                            src={message.media_url} 
                            alt="GIF" 
                            className="rounded-lg max-w-full max-h-48 object-contain"
                            loading="lazy"
                          />
                        ) : message.message_type === 'image' || message.media_url ? (
                          <div>
                            <img 
                              src={message.media_url?.startsWith('/api') ? `${BACKEND_URL}${message.media_url}` : message.media_url} 
                              alt="Shared" 
                              className="rounded-lg max-w-full max-h-64 object-cover cursor-pointer"
                              onClick={() => window.open(message.media_url?.startsWith('/api') ? `${BACKEND_URL}${message.media_url}` : message.media_url, '_blank')}
                            />
                            {message.content && <p className="mt-2 text-sm">{message.content}</p>}
                          </div>
                        ) : (
                          <p className="whitespace-pre-wrap">{message.content}</p>
                        )}
                      </div>
                      
                      {/* Reactions */}
                      {message.reactions && Object.keys(message.reactions).length > 0 && (
                        <div className={`flex gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                          {Object.entries(message.reactions).map(([emoji, users]) => (
                            <button
                              key={emoji}
                              onClick={() => handleReaction(message.id, emoji)}
                              className={`text-xs px-2 py-0.5 rounded-full border ${
                                users.includes(user.id) 
                                  ? 'bg-violet-100 border-violet-300 dark:bg-violet-900/30 dark:border-violet-700' 
                                  : 'bg-muted border-border'
                              }`}
                            >
                              {emoji} {users.length}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Quick react */}
                      <div className={`flex gap-1 mt-1 opacity-0 hover:opacity-100 transition-opacity ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        {EMOJI_LIST.slice(0, 4).map(emoji => (
                          <button
                            key={emoji}
                            onClick={() => handleReaction(message.id, emoji)}
                            className="text-sm hover:scale-125 transition-transform"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Emoji/Sticker/GIF Picker */}
      <AnimatePresence>
        {(showEmojis || showStickers || showGifs) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="bg-card border-t border-border"
          >
            {/* Tabs */}
            <div className="flex gap-1 p-2 border-b border-border">
              <button
                onClick={() => { setPickerTab('emoji'); setShowEmojis(true); setShowStickers(false); setShowGifs(false); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showEmojis ? 'bg-violet-600 text-white' : 'hover:bg-muted'
                }`}
                data-testid="picker-emoji-tab"
              >
                😀 Emoji
              </button>
              <button
                onClick={() => { setPickerTab('sticker'); setShowStickers(true); setShowEmojis(false); setShowGifs(false); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showStickers ? 'bg-violet-600 text-white' : 'hover:bg-muted'
                }`}
                data-testid="picker-sticker-tab"
              >
                🎊 Sticker
              </button>
              <button
                onClick={() => { setPickerTab('gif'); setShowGifs(true); setShowEmojis(false); setShowStickers(false); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showGifs ? 'bg-violet-600 text-white' : 'hover:bg-muted'
                }`}
                data-testid="picker-gif-tab"
              >
                <Sparkles className="w-4 h-4 inline mr-1" /> GIF
              </button>
            </div>

            <div className="max-w-4xl mx-auto p-3">
              {/* Emoji Grid */}
              {showEmojis && (
                <div className="flex flex-wrap gap-2">
                  {EMOJI_LIST.map((emoji, i) => (
                    <button
                      key={i}
                      onClick={() => setMessageText(messageText + emoji)}
                      className="text-2xl hover:scale-125 transition-transform p-2 rounded-lg hover:bg-muted"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}

              {/* Sticker Grid */}
              {showStickers && (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {STICKERS.map((sticker, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(sticker.emoji, 'sticker')}
                      className="flex flex-col items-center p-3 rounded-xl hover:bg-muted transition-colors"
                    >
                      <span className="text-3xl mb-1">{sticker.emoji}</span>
                      <span className="text-xs text-muted-foreground">{sticker.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* GIF Picker */}
              {showGifs && (
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={gifSearch}
                      onChange={(e) => setGifSearch(e.target.value)}
                      placeholder="Search GIFs..."
                      className="pl-10 rounded-full"
                      data-testid="gif-search-input"
                      autoFocus
                    />
                  </div>
                  
                  {loadingGifs ? (
                    <div className="flex justify-center py-8">
                      <div className="w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : gifs.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                      {gifs.map((gif) => (
                        <button
                          key={gif.id}
                          onClick={() => sendGif(gif.url)}
                          className="rounded-lg overflow-hidden hover:ring-2 hover:ring-violet-500 transition-all"
                        >
                          <img 
                            src={gif.preview} 
                            alt={gif.title} 
                            className="w-full h-24 object-cover"
                            loading="lazy"
                          />
                        </button>
                      ))}
                    </div>
                  ) : gifSearch ? (
                    <p className="text-center text-muted-foreground py-8">No GIFs found. Try another search!</p>
                  ) : (
                    <div className="text-center py-8">
                      <Sparkles className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">Search for GIFs to share!</p>
                      <p className="text-xs text-muted-foreground mt-1">Try: happy, celebration, money, dance</p>
                    </div>
                  )}
                  
                  <p className="text-xs text-muted-foreground text-center">Powered by Tenor</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageSelect}
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
      />

      {/* Image Preview Dialog */}
      <Dialog open={showImagePreview} onOpenChange={setShowImagePreview}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Send Image</span>
              <Button variant="ghost" size="icon" onClick={cancelImageUpload}>
                <X className="w-4 h-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {imagePreviewUrl && (
              <div className="relative rounded-xl overflow-hidden bg-muted">
                <img 
                  src={imagePreviewUrl} 
                  alt="Preview" 
                  className="w-full max-h-64 object-contain"
                />
              </div>
            )}
            <Input
              value={imageCaption}
              onChange={(e) => setImageCaption(e.target.value)}
              placeholder="Add a caption (optional)..."
              className="rounded-xl"
            />
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 rounded-full"
                onClick={cancelImageUpload}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 rounded-full gradient-primary"
                onClick={handleImageUpload}
                disabled={uploadingImage}
              >
                {uploadingImage ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-4 h-4" /> Send
                  </span>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Input */}
      <div className="bg-card/80 backdrop-blur-xl border-t border-border px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => { 
              const isOpen = showEmojis || showStickers || showGifs;
              if (isOpen) {
                setShowEmojis(false);
                setShowStickers(false);
                setShowGifs(false);
              } else {
                setShowEmojis(true);
              }
            }}
            className={(showEmojis || showStickers || showGifs) ? 'text-violet-600' : ''}
            data-testid="emoji-picker-btn"
          >
            <Smile className="w-5 h-5" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            data-testid="chat-image-btn"
          >
            <Image className="w-5 h-5" />
          </Button>
          <Input
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 rounded-full"
            data-testid="chat-input"
          />
          <Button
            onClick={() => sendMessage(messageText)}
            disabled={sending || !messageText.trim()}
            size="icon"
            className="rounded-full gradient-primary"
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GroupChat;
